import { HelpSection, HelpCategory, Role, HelpFAQ, HelpGuide } from '../types';

// ヘルプカテゴリの定義
export const helpCategories: HelpCategory[] = [
  { id: 'basic', name: '基本操作', icon: 'BookOpenIcon', color: 'blue', order: 1 },
  { id: 'dispatch', name: '配車業務', icon: 'TruckIcon', color: 'green', order: 2 },
  { id: 'ai', name: 'AI配車', icon: 'SparklesIcon', color: 'purple', order: 3 },
  { id: 'master', name: 'マスターデータ', icon: 'DatabaseIcon', color: 'orange', order: 4 },
  { id: 'reports', name: 'レポート', icon: 'ChartBarIcon', color: 'indigo', order: 5 },
  { id: 'line', name: 'LINE通知', icon: 'ChatBubbleLeftRightIcon', color: 'green', order: 6 },
  {
    id: 'troubleshoot',
    name: 'トラブル対応',
    icon: 'ExclamationTriangleIcon',
    color: 'red',
    order: 7,
  },
  { id: 'faq', name: 'よくある質問', icon: 'QuestionMarkCircleIcon', color: 'yellow', order: 8 },
  { id: 'guide', name: '体系的ガイド', icon: 'AcademicCapIcon', color: 'teal', order: 9 },
];

// 役割別ヘルプセクションの定義
export const helpSections: HelpSection[] = [
  // 基本操作 - 全役割共通
  {
    id: 'login-basic',
    title: 'ログインとダッシュボード',
    content: `## システムへのログイン

### ログイン方法
1. ブラウザでラクハイにアクセス
2. 役割を選択（モックモード）またはメール・パスワードを入力（本番モード）
3. 「ログイン」ボタンをクリック

### ダッシュボードの見方
- **案件一覧**: 本日の配車案件が表示されます
- **ステータス別表示**: 未割当、割当済、輸送中、完了の案件を色分けで確認
- **サイドバーメニュー**: 各機能へのアクセスが可能

### 画面の操作
- **レスポンシブ対応**: スマートフォン・タブレットでも快適に操作可能
- **ダークモード**: 右上のボタンで切り替え可能`,
    category: helpCategories[0], // basic
    roles: [Role.ADMIN, Role.DISPATCHER, Role.DRIVER, Role.READ_ONLY],
    order: 1,
    searchKeywords: ['ログイン', 'ダッシュボード', '画面', '基本', 'アクセス'],
  },

  // 配車業務 - 配車担当者・管理者
  {
    id: 'manual-dispatch',
    title: '手動配車の操作方法',
    content: `## 手動配車システム

### 案件の確認
1. サイドバーの「案件管理」をクリック
2. 日付選択で特定の日の案件を絞り込み
3. 案件カードをクリックして詳細を確認

### 手動配車の手順
1. **未割当案件の選択**: 赤色で表示される未割当案件を選択
2. **陸送員の選択**: 右側の陸送員一覧から適切な陸送員を選択
3. **ドラッグ&ドロップ**: 案件を陸送員にドラッグして配車
4. **確認**: 配車後、ステータスが「割当済」に変更されることを確認

### 配車時の注意点
- **車種適合性**: 陸送員が対応可能な車種か確認
- **勤務時間**: 陸送員の勤務時間内か確認
- **地理的距離**: 効率的なルートになるか考慮
- **負荷分散**: 特定の陸送員に偏らないよう配慮`,
    category: helpCategories[1], // dispatch
    roles: [Role.ADMIN, Role.DISPATCHER],
    pageContext: 'cases',
    order: 1,
    searchKeywords: ['手動配車', 'ドラッグ', 'ドロップ', '案件', '陸送員', '割当'],
  },

  // AI配車 - 配車担当者・管理者
  {
    id: 'ai-dispatch',
    title: 'AI自動配車の利用方法',
    content: `## AI自動配車システム

### AI配車の特徴
- **最適化アルゴリズム**: 地理的近接性と効率性を考慮した自動配車
- **制約条件の考慮**: 車種適合性、勤務時間、陸送員の制限事項を自動チェック
- **負荷分散**: 陸送員間の公平な配車を実現

### 使用手順
1. **サイドバーの「AI自動配車」をクリック**
2. **現状確認**: 未割当案件数と対応可能陸送員数を確認
3. **「AI配車計画を生成」ボタンをクリック**
4. **生成された配車計画を確認**:
   - 各陸送員への割当案件
   - 配車理由の説明
   - 効率性指標
5. **「配車計画を適用」ボタンで確定**

### AI配車後の調整
- AI配車は完璧ではありません
- 必要に応じて手動で調整が可能
- 緊急案件や特殊事情は手動配車を推奨`,
    category: helpCategories[2], // ai
    roles: [Role.ADMIN, Role.DISPATCHER],
    pageContext: 'schedule',
    order: 1,
    searchKeywords: ['AI配車', '自動配車', '最適化', '生成', '計画'],
  },

  // マスターデータ管理 - 管理者のみ
  {
    id: 'master-data-management',
    title: 'マスターデータの管理',
    content: `## マスターデータ管理

### アクセス権限
管理者権限が必要です。サイドバーのマスターデータメニューからアクセスできます。

### 顧客管理
1. **新規顧客の追加**:
   - 会社名、住所、連絡先、担当者情報を入力
   - ステータス（アクティブ/非アクティブ）を設定
2. **既存顧客の編集**:
   - 顧客一覧から編集対象を選択
   - 情報を更新して保存

### 陸送員管理
1. **陸送員情報の登録**:
   - 基本情報（名前、電話番号、住所）
   - 対応可能車種の設定
   - 勤務日・勤務時間の設定
   - 特殊条件（大型車免許、夜勤対応等）

### 車両情報管理
- 車両モデル、車種タイプの管理
- メーカー情報の管理
- 車両ステータスの更新`,
    category: helpCategories[3], // master
    roles: [Role.ADMIN],
    pageContext: 'master',
    order: 1,
    searchKeywords: ['マスター', 'データ', '顧客', '陸送員', '車両', '管理'],
  },

  // レポート機能 - 全役割（閲覧権限に応じて）
  {
    id: 'reports-usage',
    title: 'レポート機能の使い方',
    content: `## レポート機能

### アクセス方法
サイドバーの「レポート」メニューからアクセスできます。

### 配車実績レポート
- **期間選択**: 開始日と終了日を指定
- **フィルタリング**: 陸送員、顧客、ステータス別で絞り込み
- **データ表示**: テーブル形式で配車実績を表示

### CSVエクスポート
1. レポート画面で「CSVエクスポート」ボタンをクリック
2. ダウンロードされたファイルをExcelで開いて活用

### レポートの活用
- **請求データ**: 顧客別の配車実績で請求書作成
- **陸送員稼働**: 陸送員別の稼働実績で給与計算
- **業務分析**: 配車効率や傾向の分析

### 権限による制限
- **管理者**: 全データへのアクセス可能
- **配車担当者**: 基本的な配車実績のみ
- **閲覧専用**: レポート閲覧のみ（編集不可）`,
    category: helpCategories[4], // reports
    roles: [Role.ADMIN, Role.DISPATCHER, Role.READ_ONLY],
    pageContext: 'reports',
    order: 1,
    searchKeywords: ['レポート', 'CSV', 'エクスポート', '実績', '請求', '分析'],
  },

  // LINE通知 - ドライバー向け
  {
    id: 'line-notifications',
    title: 'LINE通知の受信と応答',
    content: `## LINE通知システム

### 通知の受信
配車が割り当てられると、LINE Botから通知が届きます。

### 通知内容
- **案件情報**: 引取・搬入先の詳細
- **作業時間**: 引取・搬入の予定時間
- **車両情報**: 対象車両の詳細
- **連絡先**: 顧客の連絡先情報

### 応答操作
1. **受諾**: 配車を受け入れる場合は「受諾」をタップ
2. **辞退**: 対応困難な場合は「辞退」をタップ
3. **後で回答**: 即座に判断できない場合の選択肢

### 応答後の流れ
- **受諾後**: 案件詳細がシステムに反映され、作業開始可能
- **辞退後**: 他の陸送員に再配車されます

### 緊急時の連絡
- トラブル発生時は通知内の緊急連絡先に電話
- LINE Botでの簡易連絡も可能`,
    category: helpCategories[5], // line
    roles: [Role.DRIVER],
    order: 1,
    searchKeywords: ['LINE', '通知', '受諾', '辞退', '応答', 'Bot'],
  },

  // ドライバー専用画面
  {
    id: 'driver-dashboard',
    title: 'ドライバー専用画面の使い方',
    content: `## ドライバー専用画面

### アクセス方法
ドライバー権限でログインすると、自動的にドライバー専用画面が表示されます。

### 画面の構成
- **本日の配車**: 当日の割当案件一覧
- **案件詳細**: 引取・搬入先の詳細情報
- **進行状況**: 作業の進捗管理

### 案件の確認
1. **案件カードをタップ**: 詳細情報を表示
2. **地図表示**: 引取・搬入先の位置を確認
3. **連絡先**: タップで直接電話をかけることが可能

### 状況更新
- **作業開始**: 引取開始時にステータス更新
- **輸送中**: 車両引取完了後にステータス更新
- **完了**: 搬入完了後にステータス更新

### 作業報告
- 車両の状態報告（必要に応じて写真添付）
- 特記事項や問題点の記録`,
    category: helpCategories[0], // basic
    roles: [Role.DRIVER],
    pageContext: 'dashboard',
    order: 2,
    searchKeywords: ['ドライバー', '専用画面', '案件', '詳細', '進捗', '報告'],
  },

  // トラブル対応 - 全役割
  {
    id: 'troubleshooting',
    title: 'トラブル時の対応方法',
    content: `## トラブル対応ガイド

### よくある問題と対処法

#### ログインできない場合
1. **ブラウザの確認**: 最新版のブラウザを使用しているか確認
2. **キャッシュクリア**: ブラウザのキャッシュをクリア
3. **認証情報の確認**: メール・パスワードが正しいか確認

#### 画面が正しく表示されない
1. **ブラウザの更新**: F5キーまたは更新ボタンで画面をリフレッシュ
2. **JavaScript有効化**: ブラウザのJavaScriptが有効になっているか確認
3. **互換性確認**: 推奨ブラウザ（Chrome、Firefox、Safari）を使用

#### AI配車が動作しない
1. **データの確認**: 未割当案件と利用可能陸送員がいるか確認
2. **ネットワーク確認**: インターネット接続が安定しているか確認
3. **システム状況**: 一時的なサーバー負荷の可能性

#### LINE通知が届かない
1. **Bot友達追加**: LINE Botが友達追加されているか確認
2. **通知設定**: LINEアプリの通知設定を確認
3. **ネットワーク**: スマートフォンのネットワーク接続を確認

### 緊急時の連絡先
- **システム管理者**: 内線XXX
- **技術サポート**: support@fleetpilot-ai.com
- **営業時間外**: 緊急連絡先（管理者より別途案内）

### 問題報告時の情報
以下の情報をお伝えください：
- 発生日時
- 使用ブラウザ・バージョン
- 操作手順
- エラーメッセージ（あれば）
- スクリーンショット（可能であれば）`,
    category: helpCategories[6], // troubleshoot
    roles: [Role.ADMIN, Role.DISPATCHER, Role.DRIVER, Role.READ_ONLY],
    order: 1,
    searchKeywords: ['トラブル', 'エラー', '問題', '対処', 'ログイン', '表示', '連絡先'],
  },

  // ダッシュボード画面のコンテキストヘルプ
  {
    id: 'dashboard-context',
    title: 'ダッシュボード画面の使い方',
    content: `## ダッシュボード画面

### 画面構成
- **案件一覧**: 本日の配車案件をステータス別に表示
- **クイックアクション**: よく使う操作への直接アクセス
- **統計情報**: 配車状況の概要を表示

### ステータス表示
- **赤色 (未割当)**: 配車が必要な案件
- **青色 (割当済)**: 陸送員に配車済みの案件
- **緑色 (輸送中)**: 作業中の案件
- **グレー (完了)**: 完了した案件

### 操作のヒント
- 案件カードをクリックすると詳細情報を確認できます
- 「案件管理」から新しい案件を追加できます
- 「スケジュール」で全体的な配車状況を確認できます`,
    category: helpCategories[0], // basic
    roles: [Role.ADMIN, Role.DISPATCHER, Role.DRIVER, Role.READ_ONLY],
    pageContext: 'dashboard',
    order: 3,
    searchKeywords: ['ダッシュボード', '案件', 'ステータス', '画面', '使い方'],
  },

  // 案件管理画面のコンテキストヘルプ
  {
    id: 'cases-context',
    title: '案件管理画面の使い方',
    content: `## 案件管理画面

### 基本操作
- **新規案件作成**: 「新規案件」ボタンから案件を追加
- **案件編集**: 案件カードをクリックして詳細編集
- **配車操作**: ドラッグ&ドロップで陸送員に配車

### フィルタリング
- **日付選択**: 特定の日の案件を表示
- **ステータス別**: 未割当・割当済みなどで絞り込み
- **顧客別**: 特定の顧客の案件のみ表示

### 効率的な配車のコツ
1. 地理的近接性を考慮する
2. 陸送員の対応可能車種を確認
3. 勤務時間内での配車を心がける
4. 負荷の分散を意識する`,
    category: helpCategories[1], // dispatch
    roles: [Role.ADMIN, Role.DISPATCHER],
    pageContext: 'cases',
    order: 2,
    searchKeywords: ['案件管理', '配車', 'ドラッグ', 'フィルタ', '新規'],
  },

  // スケジュール画面のコンテキストヘルプ
  {
    id: 'schedule-context',
    title: 'スケジュール画面の使い方',
    content: `## スケジュール画面

### 画面の見方
- **時間軸表示**: 各陸送員の1日のスケジュールを時間軸で表示
- **案件ブロック**: 各案件が時間帯ごとにブロック表示
- **空き時間**: 配車可能な時間帯が確認できる

### 操作方法
- **案件の移動**: ドラッグ&ドロップで時間や陸送員を変更
- **案件詳細**: 案件ブロックをクリックで詳細確認
- **拡大・縮小**: 時間軸の表示倍率を調整

### AI配車機能
- 「AI配車計画を生成」で最適化された配車計画を作成
- 生成後の手動調整も可能
- 制約条件を自動で考慮した配車を実行`,
    category: helpCategories[1], // dispatch
    roles: [Role.ADMIN, Role.DISPATCHER, Role.DRIVER],
    pageContext: 'schedule',
    order: 2,
    searchKeywords: ['スケジュール', '時間軸', 'AI配車', '陸送員', '最適化'],
  },

  // マスターデータ画面のコンテキストヘルプ
  {
    id: 'master-context',
    title: 'マスターデータ画面の使い方',
    content: `## マスターデータ管理画面

### 管理できるデータ
- **取引先**: 顧客情報の管理
- **陸送員**: 陸送員の基本情報・対応可能車種・勤務時間
- **車両モデル**: 扱う車両の種類・メーカー情報

### データ登録のポイント
1. **正確な情報**: 住所・連絡先は正確に入力
2. **陸送員の制約**: 対応可能車種・勤務時間を適切に設定
3. **定期更新**: 情報の鮮度を保つため定期的に更新

### 注意事項
- マスターデータは配車の基盤となる重要な情報です
- 変更時は既存案件への影響を確認してください
- バックアップを定期的に取ることを推奨します`,
    category: helpCategories[3], // master
    roles: [Role.ADMIN],
    pageContext: 'master',
    order: 2,
    searchKeywords: ['マスター', 'データ', '管理', '取引先', '陸送員', '車両'],
  },

  // ユーザー管理 - 管理者専用
  {
    id: 'user-management',
    title: 'ユーザー管理システム',
    content: `## ユーザー管理システム

### アクセス方法
1. サイドバーの「ユーザー管理」をクリック
2. 管理者権限が必要です（ADMIN role）

### 新規ユーザー登録
1. **「新規ユーザー追加」ボタンをクリック**
2. **基本情報を入力**:
   - ユーザー名: 会社内で重複チェック機能あり
   - メールアドレス: 形式チェック・重複チェック
   - 役割: 管理者/配車担当/陸送員/閲覧専用から選択
3. **パスワード設定**:
   - 8文字以上、英数字・特殊文字含む
   - 確認パスワード: リアルタイム一致確認
4. **リアルタイム検証**:
   - ✅ 緑のチェックマーク: 入力OK
   - ❌ 赤のエラー: 修正が必要
5. **「保存」ボタンで登録完了**

### ユーザー編集・更新
1. **ユーザー一覧から編集対象を選択**
2. **編集可能項目**:
   - ユーザー名（重複チェック・自分除外）
   - メールアドレス
   - 役割変更
   - アカウント状態（有効/無効）
3. **パスワードリセット**: 管理者による強制変更可能
4. **権限即座反映**: 役割変更後、即座にアクセス権限更新

### 検索・フィルタリング
- **名前検索**: 部分一致検索
- **役割フィルタ**: 管理者/配車担当/陸送員/閲覧専用
- **ステータスフィルタ**: 有効/無効ユーザー
- **並び替え**: 名前/作成日/最終ログイン

### セキュリティ機能
- **論理削除**: 物理削除を避けた安全な無効化
- **監査ログ**: ユーザー操作履歴の記録
- **アクセス制御**: RLS による行レベルセキュリティ
- **セッション管理**: 自動ログアウト・同時ログイン制御

### 役割・権限システム
| 役割 | アクセス権限 |
|------|------------|
| **管理者** | 全機能・ユーザー管理・システム設定 |
| **配車担当者** | 案件管理・配車業務・レポート閲覧 |
| **陸送員** | 割当案件確認・作業報告・個人実績 |
| **閲覧専用** | ダッシュボード・レポート確認のみ |

### よくある問題
**Q: 名前重複エラーが消えない**
A: 500ms待機後に自動チェック。ネットワーク問題の可能性

**Q: パスワード一致確認が表示されない**
A: 確認パスワード欄に入力してください

**Q: 権限変更が反映されない**
A: ユーザーの再ログインが必要な場合があります`,
    category: helpCategories[3], // master
    roles: [Role.ADMIN],
    pageContext: 'users',
    order: 3,
    searchKeywords: [
      'ユーザー',
      '管理',
      'アカウント',
      '権限',
      '役割',
      '登録',
      '編集',
      'パスワード',
    ],
  },

  // レポート画面のコンテキストヘルプ
  {
    id: 'reports-context',
    title: 'レポート画面の使い方',
    content: `## レポート画面

### 利用可能なレポート
- **配車実績レポート**: 期間別・陸送員別の配車状況
- **請求データCSV**: 顧客別の請求データをエクスポート
- **稼働分析**: 陸送員の稼働状況や効率性の分析

### データ出力機能
- **CSV形式**: Excel等で編集可能な形式でエクスポート
- **期間指定**: 必要な期間のデータのみを抽出
- **フィルタリング**: 顧客・陸送員・ステータス別での絞り込み

### 活用シーン
- **請求書作成**: 顧客別配車実績から請求書を作成
- **給与計算**: 陸送員別稼働実績で給与計算をサポート
- **業務改善**: 配車効率や傾向を分析して業務を最適化`,
    category: helpCategories[4], // reports
    roles: [Role.ADMIN, Role.DISPATCHER, Role.READ_ONLY],
    pageContext: 'reports',
    order: 2,
    searchKeywords: ['レポート', 'CSV', 'エクスポート', '実績', '分析', '請求'],
  },
];

// FAQ（よくある質問）の定義
export const helpFAQs: HelpFAQ[] = [
  {
    id: 'faq-login-trouble',
    question: 'ログインができません。どうすればよいですか？',
    answer: `ログインできない場合、以下を順番に確認してください：

1. **メールアドレスとパスワードの確認**
   - 入力したメールアドレスに間違いがないか確認
   - パスワードの大文字・小文字、特殊文字を正確に入力

2. **ブラウザの設定確認**
   - JavaScriptが有効になっているか確認
   - Cookieが有効になっているか確認
   - ブラウザのキャッシュをクリア

3. **推奨ブラウザの使用**
   - Chrome、Firefox、Safari の最新版を使用
   - Internet Explorer は推奨しません

それでも解決しない場合は、システム管理者にお問い合わせください。`,
    category: helpCategories[0], // basic
    roles: [Role.ADMIN, Role.DISPATCHER, Role.DRIVER, Role.READ_ONLY],
    order: 1,
    searchKeywords: ['ログイン', 'パスワード', 'メール', '認証', 'エラー'],
    tags: ['ログイン', 'トラブル', '基本操作'],
  },
  {
    id: 'faq-drag-drop',
    question: 'ドラッグ&ドロップで配車ができません',
    answer: `ドラッグ&ドロップ操作がうまくいかない場合：

1. **ブラウザの確認**
   - 最新版のブラウザを使用しているか確認
   - スマートフォンの場合、長押しでドラッグ開始

2. **操作方法の確認**
   - 案件カードをクリックしてから、陸送員エリアまでドラッグ
   - ドロップ可能なエリアが青くハイライトされることを確認

3. **制約条件の確認**
   - 陸送員の対応可能車種と案件の車種が一致しているか
   - 陸送員の勤務時間内での配車か確認
   - 既に他の案件で埋まっていないか確認

4. **代替手段**
   - 案件詳細画面から手動で陸送員を選択して配車することも可能`,
    category: helpCategories[1], // dispatch
    roles: [Role.ADMIN, Role.DISPATCHER],
    pageContext: 'cases',
    order: 1,
    searchKeywords: ['ドラッグ', 'ドロップ', '配車', '操作', 'できない'],
    tags: ['配車', '操作方法', 'ドラッグ&ドロップ'],
  },
  {
    id: 'faq-line-notification',
    question: 'LINE通知が届きません',
    answer: `LINE通知が届かない場合の対処法：

1. **友達追加の確認**
   - ラクハイの公式LINE Botが友達追加されているか確認
   - ブロックしていないか確認

2. **LINE設定の確認**
   - LINE通知設定が有効になっているか確認
   - 「通知の受信」が オン になっているか確認

3. **ネットワーク環境の確認**
   - インターネット接続が安定しているか確認
   - Wi-Fi環境での受信も試してみる

4. **アプリの状態確認**
   - LINEアプリが最新版に更新されているか確認
   - アプリを一度終了して再起動

5. **システム側の確認**
   - 一時的なシステム障害の可能性もあります
   - しばらく待ってから再度確認してください`,
    category: helpCategories[5], // line
    roles: [Role.DRIVER],
    order: 1,
    searchKeywords: ['LINE', '通知', '届かない', 'Bot', '友達'],
    tags: ['LINE', '通知', 'ドライバー'],
  },
  {
    id: 'faq-ai-dispatch-failed',
    question: 'AI配車の計画生成が失敗します',
    answer: `AI配車計画の生成が失敗する主な原因と対処法：

1. **データの不備**
   - 未割当案件があるか確認
   - 対応可能な陸送員がいるか確認
   - 車種や勤務時間の制約が厳しすぎないか確認

2. **ネットワーク接続**
   - インターネット接続が安定しているか確認
   - 処理に時間がかかる場合があるので、しばらく待つ

3. **システム負荷**
   - 他のユーザーも同時に使用している場合、少し時間をおいて再試行
   - 特に月末・月初は利用が集中しやすい

4. **制約条件の緩和**
   - 一部の案件を手動で配車してから再実行
   - 陸送員の勤務時間設定を確認・調整

5. **代替手段**
   - AI配車が使用できない場合は、手動配車で対応可能`,
    category: helpCategories[2], // ai
    roles: [Role.ADMIN, Role.DISPATCHER],
    order: 1,
    searchKeywords: ['AI配車', '失敗', '生成', 'エラー', '制約'],
    tags: ['AI配車', 'エラー', '制約条件'],
  },
];

// 体系的ガイドの定義
export const helpGuides: HelpGuide[] = [
  {
    id: 'guide-getting-started',
    title: 'はじめてのラクハイ',
    description: '初回ログインから基本的な操作方法まで、ラクハイを使い始めるための完全ガイド',
    sections: [
      {
        id: 'getting-started-login',
        title: '1. ログインとダッシュボード',
        content: `## 初回ログイン

### システムアクセス
1. ブラウザで提供されたURLにアクセス
2. メールアドレスとパスワードを入力
3. 役割が正しく表示されることを確認

### ダッシュボードの見方
- **案件一覧**: 本日の配車案件が表示
- **ステータス表示**: 色分けでひと目で状況把握
  - 赤: 未割当（要対応）
  - 青: 割当済
  - 緑: 輸送中
  - グレー: 完了
- **サイドメニュー**: 各機能へのアクセス`,
        order: 1,
      },
      {
        id: 'getting-started-navigation',
        title: '2. 画面操作と設定',
        content: `## 基本的な画面操作

### ダークモード
- 右上のボタンで明暗切り替え可能
- 目の疲れを軽減したい場合に有効

### レスポンシブ対応
- スマートフォン・タブレットでも利用可能
- モバイルではハンバーガーメニューから機能にアクセス

### ヘルプ機能
- 画面右上の「？」ボタンからヘルプを表示
- 現在開いている画面に関連する情報を優先表示`,
        order: 2,
      },
    ],
    category: helpCategories[0], // basic
    roles: [Role.ADMIN, Role.DISPATCHER, Role.DRIVER, Role.READ_ONLY],
    order: 1,
    searchKeywords: ['初回', 'はじめて', 'ログイン', 'ダッシュボード', '基本'],
  },
  {
    id: 'guide-dispatch-complete',
    title: '配車業務完全ガイド',
    description: '手動配車からAI配車まで、効率的な配車業務のためのガイド',
    sections: [
      {
        id: 'dispatch-manual',
        title: '1. 手動配車の基本',
        content: `## 手動配車システム

### 事前準備
1. **案件の確認**: 当日の案件一覧を確認
2. **陸送員の状況確認**: 出勤状況と稼働状況を把握
3. **特別事項の確認**: 緊急案件や特殊条件の有無

### 配車手順
1. **案件選択**: 未割当（赤色）の案件を選択
2. **陸送員確認**: 右側リストから適切な陸送員を選択
3. **制約確認**: 
   - 車種適合性（軽自動車 → 軽自動車対応陸送員）
   - 地理的距離（効率的なルート）
   - 勤務時間（陸送員の稼働時間内）
4. **ドラッグ&ドロップ**: 案件を陸送員エリアにドラッグ
5. **確認**: ステータスが「割当済」に変更されることを確認`,
        order: 1,
      },
      {
        id: 'dispatch-ai',
        title: '2. AI配車の活用',
        content: `## AI自動配車システム

### AI配車の特徴
- **最適化アルゴリズム**: 距離・時間・負荷を総合判断
- **制約条件の自動考慮**: 人手での見落としを防止
- **公平な負荷分散**: 特定の陸送員への偏りを解消

### 使用タイミング
- **案件数が多い日**: 手動では時間がかかる場合
- **初心者の配車担当者**: 経験不足を補完
- **忙しい時間帯**: 迅速な初期配車が必要な場合

### 実行手順
1. 「AI自動配車」メニューをクリック
2. 未割当案件と陸送員の確認
3. 「AI配車計画を生成」ボタンを実行
4. 生成された計画の内容確認
5. 必要に応じて手動調整
6. 「配車計画を適用」で確定

### 注意点
- AI配車は完璧ではありません
- 生成後の手動調整は推奨されます
- 特殊事情は手動で対応してください`,
        order: 2,
      },
    ],
    category: helpCategories[1], // dispatch
    roles: [Role.ADMIN, Role.DISPATCHER],
    order: 1,
    searchKeywords: ['配車', '手動', 'AI', 'ガイド', '完全'],
  },
];

// ヘルプセクションをIDで取得する関数
export const getHelpSectionById = (id: string): HelpSection | undefined => {
  return helpSections.find(section => section.id === id);
};

// 役割に基づいてフィルタリングされたセクションを取得する関数
export const getHelpSectionsByRole = (role: Role, pageContext?: string): HelpSection[] => {
  return helpSections
    .filter(section => {
      const hasRole = section.roles.includes(role);
      const hasPageContext =
        !section.pageContext || !pageContext || section.pageContext === pageContext;
      return hasRole && hasPageContext;
    })
    .sort((a, b) => a.order - b.order);
};

// カテゴリに基づいてセクションを取得する関数
export const getHelpSectionsByCategory = (categoryId: string, role: Role): HelpSection[] => {
  return helpSections
    .filter(section => section.category.id === categoryId && section.roles.includes(role))
    .sort((a, b) => a.order - b.order);
};

// FAQ関連の取得関数
export const getHelpFAQsByRole = (role: Role, pageContext?: string): HelpFAQ[] => {
  return helpFAQs
    .filter(faq => {
      const hasRole = faq.roles.includes(role);
      const hasPageContext = !faq.pageContext || !pageContext || faq.pageContext === pageContext;
      return hasRole && hasPageContext;
    })
    .sort((a, b) => a.order - b.order);
};

export const getHelpFAQsByCategory = (categoryId: string, role: Role): HelpFAQ[] => {
  return helpFAQs
    .filter(faq => faq.category.id === categoryId && faq.roles.includes(role))
    .sort((a, b) => a.order - b.order);
};

// ガイド関連の取得関数
export const getHelpGuidesByRole = (role: Role): HelpGuide[] => {
  return helpGuides.filter(guide => guide.roles.includes(role)).sort((a, b) => a.order - b.order);
};

export const getHelpGuidesByCategory = (categoryId: string, role: Role): HelpGuide[] => {
  return helpGuides
    .filter(guide => guide.category.id === categoryId && guide.roles.includes(role))
    .sort((a, b) => a.order - b.order);
};

export const getHelpGuideById = (id: string): HelpGuide | undefined => {
  return helpGuides.find(guide => guide.id === id);
};
